<template>
  <BaseModal :is-open="isOpen" title="Spectators" max-width="600px" @close="handleClose">
    <div class="spectator-list-container">
      <!-- Empty State -->
      <div v-if="spectators.length === 0" class="empty-state">
        <div class="empty-icon">
          <span class="material-symbols-outlined">visibility_off</span>
        </div>
        <p class="empty-message">No spectators are currently watching this game</p>
      </div>

      <!-- Spectator List -->
      <div v-else class="spectators-list">
        <p class="list-count">{{ spectators.length }} spectator{{ spectators.length > 1 ? 's' : '' }} watching</p>

        <div class="spectator-grid">
          <div
            v-for="spectator in spectators"
            :key="spectator.id"
            class="spectator-card"
          >
            <!-- Spectator Avatar -->
            <div class="spectator-avatar">
              <img
                v-if="spectator.profile_picture"
                :src="`/images/profiles/${spectator.profile_picture}.png`"
                :alt="spectator.name"
                class="avatar-image"
              />
              <div v-else class="avatar-initials">
                {{ getInitials(spectator.name) }}
              </div>
            </div>

            <!-- Spectator Info -->
            <div class="spectator-info">
              <h4 class="spectator-name">{{ spectator.display_name || spectator.name }}</h4>
              <p class="spectator-meta">
                <span class="material-symbols-outlined">schedule</span>
                <span>Joined {{ formatJoinTime(spectator.joined_at) }}</span>
              </p>
            </div>

            <!-- Admit Button (Creator only, if seats available) -->
            <button
              v-if="canAdmitSpectators && hasEmptySeats"
              @click="handleAdmit(spectator)"
              class="btn-admit"
              :title="`Admit ${spectator.name} to game`"
            >
              <span class="material-symbols-outlined">person_add</span>
              <span>Admit</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Info Footer -->
      <div class="info-footer">
        <div v-if="!hasEmptySeats" class="info-message">
          <span class="material-symbols-outlined">info</span>
          <span>All seats are currently filled. Spectators can join when a seat becomes available.</span>
        </div>
        <div v-else-if="canAdmitSpectators" class="info-message">
          <span class="material-symbols-outlined">info</span>
          <span>Click "Admit" to invite a spectator to join the game.</span>
        </div>
      </div>

      <!-- Close Button -->
      <button @click="handleClose" class="btn-close">
        <span>Close</span>
      </button>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import BaseModal from './BaseModal.vue'

/**
 * SpectatorListModal Component
 *
 * Displays list of spectators watching the game.
 * Allows creator to admit spectators to open seats.
 *
 * Props:
 * - spectators: Array of spectator objects
 * - is_creator: Whether current user is game creator
 * - empty_seats: Array of empty seat indices
 *
 * Emits:
 * - admit: { spectator_id: string, spectator_name: string } - Admit spectator
 * - close: [] - Modal closed
 */

export interface Spectator {
  id: string
  name: string
  display_name?: string
  profile_picture?: string
  joined_at?: string
}

interface Props {
  isOpen: boolean
  data: {
    spectators?: Spectator[]
    is_creator?: boolean
    empty_seats?: number[]
  } | null
}

const props = withDefaults(defineProps<Props>(), {
  isOpen: false,
  data: null,
})

const emit = defineEmits<{
  admit: [{ spectator_id: string; spectator_name: string }]
  close: []
}>()

/**
 * Spectators list
 */
const spectators = computed(() => props.data?.spectators || [])

/**
 * Whether current user can admit spectators
 */
const canAdmitSpectators = computed(() => props.data?.is_creator || false)

/**
 * Whether there are empty seats
 */
const hasEmptySeats = computed(() => {
  const emptySeats = props.data?.empty_seats || []
  return emptySeats.length > 0
})

/**
 * Get initials from name
 */
function getInitials(name: string): string {
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

/**
 * Format join time (relative)
 */
function formatJoinTime(timestamp?: string): string {
  if (!timestamp) return 'recently'

  const joinTime = new Date(timestamp).getTime()
  const now = Date.now()
  const diff = now - joinTime

  const seconds = Math.floor(diff / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)

  if (hours > 0) return `${hours}h ago`
  if (minutes > 0) return `${minutes}m ago`
  return 'just now'
}

/**
 * Handle admit spectator
 */
function handleAdmit(spectator: Spectator): void {
  emit('admit', {
    spectator_id: spectator.id,
    spectator_name: spectator.display_name || spectator.name,
  })
}

/**
 * Handle close
 */
function handleClose(): void {
  emit('close')
}
</script>

<style scoped>
.spectator-list-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 0.5rem;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 3rem 1rem;
  text-align: center;
}

.empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-icon .material-symbols-outlined {
  font-size: 3rem;
  color: rgba(255, 255, 255, 0.3);
}

.empty-message {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
}

/* Spectators List */
.spectators-list {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.list-count {
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
  margin: 0;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
}

.spectator-grid {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 0.5rem;
}

/* Custom Scrollbar */
.spectator-grid::-webkit-scrollbar {
  width: 6px;
}

.spectator-grid::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 3px;
}

.spectator-grid::-webkit-scrollbar-thumb {
  background: rgba(232, 197, 71, 0.3);
  border-radius: 3px;
}

.spectator-grid::-webkit-scrollbar-thumb:hover {
  background: rgba(232, 197, 71, 0.5);
}

/* Spectator Card */
.spectator-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  transition: all 0.2s;
}

.spectator-card:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.15);
}

.spectator-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  border: 2px solid rgba(255, 255, 255, 0.2);
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-initials {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(232, 197, 71, 0.6), rgba(212, 175, 55, 0.6));
  color: #0d1f2d;
  font-size: 1.125rem;
  font-weight: 700;
}

.spectator-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.spectator-name {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
}

.spectator-meta {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
}

.spectator-meta .material-symbols-outlined {
  font-size: 0.875rem;
}

.btn-admit {
  padding: 0.625rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.8125rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  border: none;
  background: linear-gradient(135deg, #e8c547 0%, #d4af37 100%);
  color: #0d1f2d;
  box-shadow: 0 2px 8px rgba(232, 197, 71, 0.2);
  flex-shrink: 0;
}

.btn-admit:hover {
  background: linear-gradient(135deg, #f0d060 0%, #e0bf47 100%);
  box-shadow: 0 4px 12px rgba(232, 197, 71, 0.3);
  transform: translateY(-1px);
}

.btn-admit:active {
  transform: translateY(0);
}

.btn-admit .material-symbols-outlined {
  font-size: 1.125rem;
}

/* Info Footer */
.info-footer {
  padding-top: 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.info-message {
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  padding: 0.875rem;
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 8px;
  font-size: 0.875rem;
  color: rgba(147, 197, 253, 0.9);
  line-height: 1.5;
}

.info-message .material-symbols-outlined {
  font-size: 1.125rem;
  flex-shrink: 0;
  margin-top: 0.125rem;
}

/* Close Button */
.btn-close {
  padding: 0.875rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.btn-close:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

.btn-close:active {
  transform: translateY(0);
}

/* Responsive */
@media (max-width: 640px) {
  .spectator-card {
    flex-direction: column;
    text-align: center;
  }

  .spectator-info {
    align-items: center;
  }

  .btn-admit {
    width: 100%;
    justify-content: center;
  }
}
</style>
